/**
 * Utility function to copy text to clipboard with fallback for Firefox and other browsers
 * that have strict clipboard API requirements.
 *
 *
 * @param text - The text to copy to clipboard
 * @throws {Error} If both modern and legacy clipboard methods fail
 */
export const copyToClipboardWithFallback = async (
  text: string
): Promise<void> => {
  if (!text || typeof text !== 'string') {
    throw new Error('Invalid text provided for clipboard copy');
  }

  // First try the modern clipboard API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (err: unknown) {
      // Modern API failed, fall back to legacy method
    }
  }

  // Use a span element approach that doesn't trigger focus events
  let tempElement: HTMLElement | null = null;
  let range: Range | null = null;

  try {
    // Create a temporary span element instead of textarea to avoid focus issues
    tempElement = document.createElement('span');
    tempElement.style.cssText = `
      position: fixed !important;
      left: -9999px !important;
      top: -9999px !important;
      opacity: 0 !important;
      pointer-events: none !important;
      z-index: -9999 !important;
      width: 1px !important;
      height: 1px !important;
      overflow: hidden !important;
      white-space: pre !important;
    `;
    tempElement.textContent = text;
    tempElement.setAttribute('aria-hidden', 'true');

    document.body.appendChild(tempElement);

    // Create a selection range
    const selection = window.getSelection();
    range = document.createRange();

    range.selectNodeContents(tempElement);

    // Clear any existing selection and select our text
    selection?.removeAllRanges();
    selection?.addRange(range);

    const successful = document.execCommand('copy');

    if (!successful) {
      throw new Error('Legacy copy method failed');
    }
  } catch (legacyErr) {
    throw new Error(
      `Clipboard copy failed: ${
        legacyErr instanceof Error ? legacyErr.message : String(legacyErr)
      }`
    );
  } finally {
    // Ensure cleanup happens even if there's an error - prevents memory leaks
    try {
      const selection = window.getSelection();
      selection?.removeAllRanges();

      if (range) {
        range.detach();
      }

      if (tempElement && tempElement.parentNode) {
        document.body.removeChild(tempElement);
      }
    } catch (cleanupErr) {
      // Ignore cleanup errors to prevent cascading issues
    }
  }
};
